import { useCallback, useState } from 'react';
import type { Result } from '@api';
import { Failure, Success, useErrorMessage, useDatahubAPI } from '@api';
import { useNotification } from '@data/notification';
import type { AxiosResponse, AxiosError } from 'axios';
import axios from 'axios';
import { projectAtom } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import { useTranslation } from 'react-i18next';
import type { DiagName } from '@data/datahub/vehicleItem/types';
import { useAtomCallback } from 'jotai/utils';

type APIResponse = {
  diagnostic_items: DiagName[];
  page_size: number;
  next_page_token?: string;
};

const useRequest = () => {
  const { datahubAPI } = useDatahubAPI({ cancelWhenUnmounting: true });

  return useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
      itemType: string,
      params?: URLSearchParams,
    ): Promise<Result<APIResponse, AxiosResponse | AxiosError>> => {
      try {
        const paramsToStr = params ? `?${params.toString()}` : '';
        const res = await datahubAPI.get(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/diagnostics/items/${itemType}${paramsToStr}`,
        );
        return new Success(res?.data);
      } catch (error) {
        return axios.isCancel(error)
          ? new Success({ diagnostic_items: [], page_size: 0 })
          : new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [datahubAPI],
  );
};

export const useVehicleItemsAPI = (): {
  loading: boolean;
  nextPageToken?: string;
  getVehicleItems: (
    vehicleId: string,
    params?: URLSearchParams,
  ) => Promise<DiagName[]>;
} => {
  const [loading, setLoading] = useState(false);
  const [nextPageToken, setNextPageToken] = useState<string | undefined>();
  const { notifyError } = useNotification();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();
  const request = useRequest();

  const getVehicleItems = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string, params?: URLSearchParams) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);

        if (!project || !environment) {
          notifyError({
            message: t('api.datahub.get_diagnostics', { status: 'failed' }),
          });
          return [];
        }

        setLoading(true);

        const res = await request(
          project.id,
          environment.environment_id,
          vehicleId,
          'name',
          params,
        ).finally(() => {
          setLoading(false);
        });

        // 取得失敗時
        if (res.isFailure()) {
          const label = t('api.datahub.get_diagnostics', {
            status: 'failed',
          });
          const errorMessage = getErrorMessage(
            res as Result<APIResponse, AxiosResponse>,
          );
          // リクエストに失敗した場合
          notifyError({ message: `${label}: ${errorMessage}` });
          return [];
        }

        setNextPageToken(res.value.next_page_token);
        return res.value.diagnostic_items;
      },
      [notifyError, request, t, getErrorMessage],
    ),
  );

  return {
    loading,
    nextPageToken,
    getVehicleItems,
  };
};
